import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSocket } from "../contexts/Socket.context";
import MusicItem from "./MusicItem";
import { useSnackbar } from "../contexts/Snackbar.context";
import { truncateString } from "../helper/utils";
import { SocketEvent, VideoDetails } from "@common/SocketEvents";
import { useAppContext } from "../contexts/App.context";
import { uniqBy } from "lodash";
import "./SearchResultPanel.scss";

interface SingerDetailModalProps {
  open: boolean;
  songTitle?: string;
  handleModalClose: () => void;
  handleModalSubmit: (data: { name: string }) => void;
}

const SingerDetailModal: React.FC<SingerDetailModalProps> = ({
  open,
  songTitle,
  handleModalClose,
  handleModalSubmit,
}) => {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const truncateTitle = (title: string, maxLength: number) => {
    return title.length > maxLength
      ? `${title.substring(0, maxLength)}...`
      : title;
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (open && inputRef.current) {
      timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    return () => clearTimeout(timer);
  }, [open]);

  const handleDialogClose = (_event: unknown, reason: string) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      // Set 'open' to false, however you would do that with your particular code.
      handleModalClose();
    }
  };

  return (
    <Dialog
      disableRestoreFocus
      open={open}
      className="dialog-container"
      onClose={handleDialogClose}
    >
      <DialogTitle>
        <small>{truncateTitle(songTitle ?? "", 40)}</small>
      </DialogTitle>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setName("");
            inputRef.current?.blur();
            handleModalSubmit({ name });
          }}
        >
          <TextField
            inputRef={inputRef}
            margin="dense"
            autoComplete="off"
            autoFocus={true}
            label="Who is singing?"
            type="text"
            autoCapitalize="words"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            inputRef.current?.blur();
            setName("");
            handleModalClose();
          }}
          color="error"
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            inputRef.current?.blur();
            setName("");
            handleModalSubmit({ name });
          }}
          color="primary"
        >
          Add to Playlist
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MAX_PAGES = 5;

interface SearchResultPanelProps {
  query: string;
  onSelectMusicItem: () => void;
}

const SearchResultPanel: React.FC<SearchResultPanelProps> = ({
  query,
  onSelectMusicItem,
}) => {
  const { sendRequest } = useSocket();
  const [blockScreen, setBlockScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageToken, setPageToken] = useState<string | undefined>();
  const [videos, setVideos] = useState<VideoDetails[]>([]);
  const [hasMore, setHasMore] = useState(true);
  // Pages loaded for the current query. Each extra page is a paid API call on
  // the server, so infinite scroll stops after MAX_PAGES.
  const pagesLoaded = useRef(0);
  // Bumped whenever the query changes (including to "" when the search closes).
  // A reply can arrive long after it was asked for: with the screen off the
  // socket drops, holds the request, and sends it on reconnect. Replies for
  // an older search are ignored instead of filling the list again.
  const searchGeneration = useRef(0);
  const observer = useRef<IntersectionObserver>();
  const addSnackbar = useSnackbar();
  const { data } = useAppContext();
  const [karakokeMode, setKaraokeMode] = useState(data.isKaraokeMode);

  // State for modal
  const [open, setOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoDetails | null>(null);
  const [searchError, setSearchError] = useState(false);

  const askWhoSing = data.playlistData?.playerSettings?.askWhoSing;

  useEffect(() => {
    setKaraokeMode(data.isKaraokeMode);
  }, [data.isKaraokeMode]);

  const onSearch = async (q: string, pt?: string) => {
    const generation = searchGeneration.current;
    const isCurrent = () => generation === searchGeneration.current;
    setIsLoading(true);
    try {
      const result = await sendRequest(SocketEvent.SEARCH, {
        query: `${q} ${karakokeMode ? "karaoke" : ""}`,
        pageToken: pt,
      });
      if (!isCurrent()) return;
      if(result.error){
        throw new Error(result.error);
      }
      setPageToken(result.nextPageToken);
      const uniqueVideos = uniqBy(result.items, "id");
      // also check any of uniqueVideos contains id existing in prevVideos, if so, filter out those videos to prevent duplicate videos in playlist
      const prevVideoIds = new Set(videos.map((video) => video.id));
      const filteredUniqueVideos = uniqueVideos.filter(
        (video) => !prevVideoIds.has(video.id)
      );
      setVideos((prevVideos) => [...prevVideos, ...filteredUniqueVideos]);
      pagesLoaded.current += 1;
      // Stop when a page adds nothing new: the list wouldn't grow, the end of
      // it would stay on screen and keep requesting pages on its own.
      setHasMore(
        result.nextPageToken != null &&
          filteredUniqueVideos.length > 0 &&
          pagesLoaded.current < MAX_PAGES
      );
    } catch {
      if (!isCurrent()) return;
      setSearchError(true);
      setHasMore(false);
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  };

  const lastVideoElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onSearch(query, pageToken);
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, hasMore]
  );

  useEffect(() => {
    searchGeneration.current += 1;
    setVideos([]);
    setPageToken(undefined);
    setSearchError(false);
    setIsLoading(false);
    setHasMore(true);
    pagesLoaded.current = 0;
    if (query.trim() !== "") {
      // umami.track("search", { query: query });
      onSearch(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    setBlockScreen(false);
  }, []);

  const handleModalClose = () => {
    setOpen(false);
    setSelectedVideo(null);
  };

  const handleModalSubmit = async (
    data: { name?: string },
    _video?: VideoDetails
  ) => {
    const targetVideo = selectedVideo || _video;
    const { name } = data;

    if (targetVideo) {
      setBlockScreen(true);
      await sendRequest(SocketEvent.ADD_TO_PLAYLIST, {
        videoDetail: { ...targetVideo, singer: name },
      });
      addSnackbar(
        `Added ${truncateString(targetVideo.title, 50)} to playlist.`,
        "success"
      );
      // umami.track("add_to_playlist", { video: targetVideo.title });
      setBlockScreen(false);
      onSelectMusicItem?.();
      handleModalClose();
    }
  };

  const onHandleAddToPlaylist = (video: VideoDetails) => {
    if (data.isKaraokeMode && askWhoSing) {
      setSelectedVideo(video);
      setOpen(true);
    } else {
      handleModalSubmit({}, video);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: (theme) => theme.spacing(1),
      }}
    >
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={blockScreen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {videos.map((video) => {
            return (
              <MusicItem
                key={video.id}
                video={video}
                onMusicItemClick={onHandleAddToPlaylist}
              />
            );
          })}

          {videos.length > 0 && (
            <div ref={lastVideoElementRef} key={"lastVideoElem"}></div>
          )}

          {/* Loading Spinner */}
          <Grid
            item
            xs={12}
            sx={{
              visibility: isLoading ? "visible" : "hidden",
              paddingTop: (theme) => theme.spacing(2),
              paddingBottom: (theme) => theme.spacing(2),
              marginTop: (theme) => theme.spacing(4),
              marginBottom: (theme) => theme.spacing(2),
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              flex: "1",
            }}
          >
            <CircularProgress color="inherit" />
          </Grid>
        </Grid>
      </Container>
      <SingerDetailModal
        open={open}
        handleModalClose={handleModalClose}
        handleModalSubmit={handleModalSubmit}
        songTitle={selectedVideo?.title}
      />
      <Dialog open={searchError} onClose={() => setSearchError(false)}>
        <DialogTitle>Search Unavailable</DialogTitle>
        <DialogContent>Something went wrong while searching. Please try again later.</DialogContent>
        <DialogActions>
          <Button onClick={() => window.location.reload()} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchResultPanel;
